// api.ts — the ONLY module that talks to the backend. No component calls fetch directly.
//
// Session: the httpOnly `qms_sid` cookie (credentials: 'include'). On a 401 a
// `qms:unauthorized` window event fires and App.tsx drops back to the login screen — a
// dead cookie must never leave the user staring at an empty dashboard.

import type {
  AgentOption, AgentRanking, AgentReport, Assignment, AssignmentList, AssignmentRunResult, DateAxis, EvaluatedAgentOption, EvaluationDetail, EvaluationList, EvaluatorOption,
  ManagedUser, Meta, ObjectionResolution, Overview, PanelUser, Role, WeeklyReportBoard,
} from './types';

// build.mjs bakes this; "" = same origin (prod: the API serves the SPA too).
declare const __API_BASE_URL__: string;
const API_BASE = (typeof __API_BASE_URL__ !== 'undefined' ? __API_BASE_URL__ : '').replace(/\/+$/, '');

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Turkish text for the error codes the API can return, so no screen prints a raw code. */
const MESSAGES: Record<string, string> = {
  unauthorized: 'Oturum sona erdi, tekrar giriş yapın.',
  forbidden: 'Bu işlem için yetkiniz yok.',
  not_found: 'Kayıt bulunamadı.',
  invalid_credentials: 'Kullanıcı adı veya şifre hatalı.',
  rate_limited: 'Çok fazla deneme yapıldı, bir dakika sonra tekrar deneyin.',
  payload_too_large: 'Dosya çok büyük.',
  audio_required: 'Her çağrıda çağrı kaydı zorunlu.',
  no_audio: 'Bu değerlendirmede ses kaydı yok.',
  agent_required: 'Agent seçilmedi.',
  agent_not_found: 'Agent bulunamadı.',
  call_at_required: 'Çağrı tarihi zorunlu.',
  bad_call_at: 'Çağrı tarihi geçersiz.',
  call_at_in_future: 'Çağrı tarihi gelecekte olamaz.',
  player_user_id_required: 'Kullanıcı ID zorunlu.',
  bad_player_user_id: 'Kullanıcı ID geçersiz.',
  username_taken: 'Bu kullanıcı adı zaten kullanılıyor.',
  password_too_short: 'Şifre çok kısa.',
  already_objected: 'Bu değerlendirmeye zaten itiraz edilmiş.',
  already_resolved: 'Bu itiraz zaten sonuçlandırılmış.',
  not_objectable: 'Bu değerlendirmeye itiraz edilemez.',
  objection_body_required: 'İtiraz metni boş olamaz.',
  scores_required: 'Puan güncellemesi için yeni puanlar gerekli.',
  cannot_deactivate_self: 'Kendi hesabınızı pasife alamazsınız.',
  empty_audio: 'Çağrı kaydı boş, dosyayı tekrar yükleyin.',
  // No number here on purpose: the cap lives in engine/audio-set.js and rides in the
  // server's own `message` for every handler-thrown case (toError prefers it). This is only
  // the fallback for the parser's 413, which answers with a bare code.
  too_many_audio: 'Bu değerlendirmeye daha fazla çağrı kaydı eklenemez.',
  unknown_audio: 'Silinmek istenen çağrı kaydı bulunamadı.',
  bad_audio_ids: 'Silinecek çağrı kayıtları okunamadı.',
  bad_brand: 'Bilinmeyen marka.',
  bad_evaluator_id: 'Değerlendirici geçersiz.',
  bad_date_by: 'Tarih ekseni geçersiz.',
  bad_active: 'Hesap durumu geçersiz.',
  bad_reply: 'Yanıt metni geçersiz.',
  bad_scores_shape: 'Puanlar okunamadı.',
  bad_criticals_shape: 'Kritik hata listesi okunamadı.',
  notes_required: '10 puan altındaki çağrılarda QMS notu zorunludur.',
  reason_required: 'Değişiklik gerekçesi zorunlu.',
  objection_open: 'Açık itirazı olan değerlendirme düzenlenemez — önce itirazı yanıtlayın.',
  list_required: 'Liste seçilmedi.',
  unknown_list: 'Bilinmeyen liste.',
  call_type_required: 'Arama tipi seçilmedi.',
  unknown_call_type: 'Bilinmeyen arama tipi.',
  bad_week: 'Hafta geçersiz.',
  week_in_future: 'Bu hafta henüz başlamadı.',
  report_body_required: 'Rapor metni boş olamaz.',
  report_too_long: 'Rapor çok uzun.',
  report_exists: 'Bu temsilci için bu haftanın raporu başka bir uzman tarafından gönderilmiş.',
  report_locked: 'Son teslim geçti, rapor artık düzenlenemez.',
  assignment_not_found: 'Atanan çağrı bulunamadı.',
  assignment_closed: 'Bu atanan çağrı zaten kapandı.',
  assignment_mismatch: 'Atanan çağrının agentı seçilen agentla aynı değil.',
  bad_assignment_id: 'Atanan çağrı geçersiz.',
  skip_reason_required: 'Atlama gerekçesi zorunlu.',
  skip_reason_too_long: 'Gerekçe çok uzun.',
  assign_disabled: 'Otomatik atama kapalı.',
  bad_day: 'Gün geçersiz.',
  internal: 'Sunucu hatası. Tekrar deneyin.',
};

async function toError(res: Response): Promise<ApiError> {
  let code = 'internal';
  let message = '';
  try {
    const body = await res.json();
    code = String(body?.error || code);
    message = String(body?.message || '');
  } catch { /* non-JSON error page */ }
  return new ApiError(res.status, code, message || MESSAGES[code] || `İstek başarısız (${res.status}).`);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    const err = await toError(res);
    // A 401 drops back to the login screen ONLY for a genuine session drop. A failed login
    // or a wrong current-password also return 401 (code 'invalid_credentials'); firing the
    // global logout there would tear down a valid session and mask the real message.
    if (res.status === 401 && err.code !== 'invalid_credentials') {
      window.dispatchEvent(new CustomEvent('qms:unauthorized'));
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function json<T>(path: string, method: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ── auth ─────────────────────────────────────────────────────────────────────

export const login = (username: string, password: string) =>
  json<PanelUser>('/api/auth/login', 'POST', { username, password });

export const logout = () => json<{ ok: true }>('/api/auth/logout', 'POST');

export const me = () => request<PanelUser>('/api/auth/me');

export const changePassword = (currentPassword: string, newPassword: string) =>
  json<{ ok: true }>('/api/auth/password', 'POST', { currentPassword, newPassword });

// ── meta ─────────────────────────────────────────────────────────────────────

export const getMeta = () => request<Meta>('/api/meta');

export const getAgents = () =>
  request<{ agents: AgentOption[] }>('/api/agents').then((r) => r.agents);

/** Staff only — the list's evaluator filter (everyone who has filed a row in scope). */
export const getEvaluators = () =>
  request<{ evaluators: EvaluatorOption[] }>('/api/evaluators').then((r) => r.evaluators);

/** Staff only — the list's agent filter (every agent with a row in scope, deactivated included). */
export const getEvaluatedAgents = () =>
  request<{ agents: EvaluatedAgentOption[] }>('/api/evaluated-agents').then((r) => r.agents);

// ── evaluations ──────────────────────────────────────────────────────────────

export interface EvaluationFilters {
  brand?: string;
  agentId?: number;
  /** Who filed the row. */
  evaluatorId?: number;
  status?: string;
  period?: string;
  /** Which instant `period` windows — the call's (default) or the evaluation's own. */
  dateBy?: DateAxis;
  criticalOnly?: boolean;
  limit?: number;
  offset?: number;
}

export const listEvaluations = (f: EvaluationFilters = {}) =>
  request<EvaluationList>(`/api/evaluations${qs(f as Record<string, string | number | boolean | undefined>)}`);

export const getEvaluation = (id: number) =>
  request<EvaluationDetail>(`/api/evaluations/${id}`);

export interface NewEvaluationInput {
  agentId: number;
  /** The called member's BackOffice user id — decimal digits. Required; the server validates. */
  playerUserId: string;
  callAt: string;          // 'YYYY-MM-DDTHH:MM' — read as Istanbul wall-clock by the API
  /** call_lists.key. The server stores the LABEL and derives the call type from it. */
  listKey: string;
  /** call_types.key — only read when the picked list has asksType. */
  callTypeKey: string;
  notes: string;
  scores: Record<string, number>;
  criticals: string[];
  /** 1–Meta.audio.maxFiles recordings, in the order they should play back. */
  audios: File[];
  /** The auto-assigned call this evaluation closes (Task Board #163), if any. */
  assignmentId?: number | null;
}

export interface NewEvaluationResult {
  id: number;
  finalScore: number;
  weighted: number;
  hasCritical: boolean;
  qualityLabel: string;
}

export async function createEvaluation(input: NewEvaluationInput): Promise<NewEvaluationResult> {
  const fd = new FormData();
  fd.set('agentId', String(input.agentId));
  fd.set('playerUserId', input.playerUserId);
  fd.set('callAt', input.callAt);
  fd.set('listKey', input.listKey);
  fd.set('callTypeKey', input.callTypeKey);
  fd.set('notes', input.notes);
  fd.set('scores', JSON.stringify(input.scores));
  fd.set('criticals', JSON.stringify(input.criticals));
  if (input.assignmentId) fd.set('assignmentId', String(input.assignmentId));
  // The files go LAST so the server has every field parsed before the big parts arrive.
  // `append`, not `set`: several parts share the field name and the server keeps their
  // order. Omitted entirely when there are none — an empty part would trip the 0-byte guard.
  for (const f of input.audios) fd.append('audio', f, f.name);
  return request<NewEvaluationResult>('/api/evaluations', { method: 'POST', body: fd });
}

/**
 * Re-score an existing evaluation. Multipart for the same reason the create call is: the
 * edit may ATTACH recordings — a row can hold up to Meta.audio.maxFiles, and a legacy row
 * that carries none must gain one before it can be re-scored.
 *
 * Attaching ADDS; removing is asked for by id in `removeAudioIds`, so a specialist fixing a
 * note can never silently drop the evidence.
 */
export async function editEvaluation(
  id: number,
  input: {
    scores: Record<string, number>; criticals: string[]; notes: string; reason: string;
    /** A note ADDED under the editor's own name (migration 011); '' = none this time. */
    note: string;
    /** 'YYYY-MM-DDTHH:MM' Istanbul wall-clock, or '' to keep the stored date. */
    callAt: string;
    /** Decimal digits, or '' to keep the stored user id. */
    playerUserId: string;
    audios: File[];
    /** EvaluationDetail.audios[].id values to delete. */
    removeAudioIds: number[];
  },
): Promise<NewEvaluationResult> {
  const fd = new FormData();
  fd.set('scores', JSON.stringify(input.scores));
  fd.set('criticals', JSON.stringify(input.criticals));
  fd.set('notes', input.notes);
  fd.set('reason', input.reason);
  // Omitted when empty — the server reads an absent field as "no added note".
  if (input.note) fd.set('note', input.note);
  // Omitted when unchanged/empty — the server reads an absent field as "keep call_at".
  if (input.callAt) fd.set('callAt', input.callAt);
  // Same convention: an absent/empty field keeps the stored user id.
  if (input.playerUserId) fd.set('playerUserId', input.playerUserId);
  if (input.removeAudioIds.length) fd.set('removeAudio', JSON.stringify(input.removeAudioIds));
  for (const f of input.audios) fd.append('audio', f, f.name);
  return request<NewEvaluationResult>(`/api/evaluations/${id}/edit`, { method: 'POST', body: fd });
}

export const deleteEvaluation = (id: number) =>
  json<{ ok: true }>(`/api/evaluations/${id}`, 'DELETE');

/**
 * Absolute URL for the <audio> element — the browser fetches it with the session cookie.
 * Takes the `url` the detail response already built (one per recording), so the SPA never
 * has to know how the route is spelled.
 */
export const audioSrc = (url: string) => `${API_BASE}${url}`;

// ── objections ───────────────────────────────────────────────────────────────

export const createObjection = (evaluationId: number, body: string) =>
  json<{ id: number; createdAt: string }>(`/api/evaluations/${evaluationId}/objection`, 'POST', { body });

export const listObjections = (state?: 'open' | 'resolved', brand?: string) =>
  request<{ objections: import('./types').ObjectionRow[]; openCount: number }>(
    `/api/objections${qs({ state, brand: brand || undefined })}`,
  );

export const resolveObjection = (
  id: number,
  payload: {
    resolution: ObjectionResolution; reply: string;
    /** A note ADDED to the evaluation under the resolver's name (migration 011); omit for none. */
    note?: string;
    scores?: Record<string, number>; criticals?: string[];
  },
) => json<{ ok: true; resolution: ObjectionResolution; finalScore: number; hasCritical: boolean; qualityLabel: string }>(
  `/api/objections/${id}/resolve`, 'POST', payload,
);

// ── reports ──────────────────────────────────────────────────────────────────

export const getOverview = (period?: string, brand?: string) =>
  request<Overview>(`/api/reports/overview${qs({ period, brand })}`);

/** Haftanın Temsilcisi · 24 saat for the Overview's period (its week; a month → this week / its last week). */
export const getAgentRanking = (period?: string, brand?: string) =>
  request<AgentRanking>(`/api/reports/agent-ranking${qs({ period, brand })}`);

/** `'me'` resolves server-side to the signed-in user — the SPA never guesses its own id. */
export const getAgentReport = (agentId: number | 'me', period?: string) =>
  request<AgentReport>(`/api/reports/agent/${agentId}${qs({ period })}`);

// ── weekly reports (owner 2026-09-29) ─────────────────────────────────────────

/** Empty week = the DUE week (last completed). */
export const getWeeklyReports = (week?: string, brand?: string) =>
  request<WeeklyReportBoard>(`/api/weekly-reports${qs({ week, brand: brand || undefined })}`);

export const saveWeeklyReport = (agentId: number, week: string, body: string) =>
  json<{ id: number; created: boolean; late: boolean }>('/api/weekly-reports', 'PUT', { agentId, week, body });

// ── assigned calls (Task Board #163) ──────────────────────────────────────────

/** Empty day = today (Istanbul). A qms always gets only their own rows (server-side). */
export const listAssignments = (day?: string, brand?: string) =>
  request<AssignmentList>(`/api/assignments${qs({ day, brand: brand || undefined })}`);

export const getAssignment = (id: number) => request<Assignment>(`/api/assignments/${id}`);

export const skipAssignment = (id: number, reason: string) =>
  json<Assignment>(`/api/assignments/${id}/skip`, 'POST', { reason });

export const reopenAssignment = (id: number) =>
  json<Assignment>(`/api/assignments/${id}/reopen`, 'POST', {});

/** Admin: hand out today's calls now (idempotent — anyone already served is skipped). */
export const runAssignments = () => json<AssignmentRunResult>('/api/assignments/run', 'POST', {});

// ── users (admin) ────────────────────────────────────────────────────────────

export const listUsers = () =>
  request<{ users: ManagedUser[] }>('/api/users').then((r) => r.users);

export const createUser = (input: {
  username: string; displayName: string; role: Role; brandKey: string | null; password: string;
}) => json<ManagedUser>('/api/users', 'POST', input);

export const updateUser = (id: number, patch: Partial<{
  displayName: string; role: Role; brandKey: string | null; active: boolean; password: string;
}>) => json<ManagedUser>(`/api/users/${id}`, 'PATCH', patch);
